import { Button, IconButton, Portal, Switch, Typography } from "@mui/material";
import { Box, styled, useTheme } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridFilterModel, GridSortModel, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid-pro";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useCallback, useEffect } from "react";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { FlagIcon } from "src/components/iconify/flagIcon";
import { useAddScrapingMutation, useDisableActiveModeMutation, useDisableKoBiddingAutoMaxCpcMutation, useDisableKoBiddingManualMaxCpcMutation, useDisableScrapingMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import ModalActivationsAuto from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-auto";
import ModalActivationsManual from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-manual";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import ModalCreateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy";
import ModalScraping from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-scraping";
import { formatNumber } from "src/utils/format-number";
import CustomLineChart from "src/components/inline-chart/inline-chart-activation";
import { t } from "i18next";
import countriesJson from "src/utils/countries.json";
import { formatCurrency } from "src/utils/format-currency";
import { FilterPanelCustom } from "../filter-panel-custom";
import ModalCreateStrategyOther from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy-other";

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

const GreenCheckSwitch = styled(Switch)(() => ({
    width: 36,
    height: 18,
    padding: 0,
    overflow: "hidden",

    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 0,
        top: 0,
        left: 0,
        transitionDuration: "200ms",

        "&.Mui-checked": {
            transform: "translateX(18px)",

            "& .MuiSwitch-thumb": {
                backgroundColor: "#1FA463",

                "&::before": {
                    content: '""',
                    width: 12,
                    height: 12,
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>\")",
                },
            },
        },
    },

    "& .MuiSwitch-thumb": {
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: "#C83737",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "&::before": {
            content: '""',
            width: 12,
            height: 12,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>\")",
        },
    },

    "& .MuiSwitch-track": {
        width: 36,
        height: 18,
        borderRadius: 9,
        backgroundColor: "#e9ecee",
        opacity: 1,
    },

    "& .MuiSwitch-switchBase + .MuiSwitch-track": {
        backgroundColor: "#e9ecee",
    },

    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#e9ecee",
    },
}));

const headerNameMap: Record<string, string> = {
    fitted_trend_top_blended_presence_rate: "Smart Top",
    fitted_trend_abs_top_blended_presence_rate: "Smart First",
    fitted_trend_top_paid_presence_rate: "Paid Top",
    fitted_trend_abs_top_paid_presence_rate: "Paid First",
};

const colorMap: Record<string, string> = {
    fitted_trend_top_blended_presence_rate: "rgb(37, 150, 190)",
    fitted_trend_abs_top_blended_presence_rate: "rgb(185, 133, 244)",
    fitted_trend_top_paid_presence_rate: "rgb(248, 204, 203)",
    fitted_trend_abs_top_paid_presence_rate: "rgb(255, 225, 128)",
};


export default function DataGridKeywordOther({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([])
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';

    const handleCloseScraping = useCallback(() => setModalScraping({ open: false, row: null }), []);
    const handleCloseActivationManual = useCallback(() => setModalActivationManual({ open: false, isActive: false, row: null }), []);
    const handleCloseActivationAuto = useCallback(() => setModalActivationAuto({ open: false, isActive: false, row: null }), []);

    const [addScraping] = useAddScrapingMutation();
    const [disableScraping] = useDisableScrapingMutation();
    const [disableActiveMode] = useDisableActiveModeMutation();
    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();
    const [disableKoBiddingAutoMaxCpc] = useDisableKoBiddingAutoMaxCpcMutation();


    const [modalScraping, setModalScraping] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalActivationManual, setModalActivationManual] = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalActivationAuto, setModalActivationAuto] = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalCreateStrategy, setModalCreateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [confirmDisable, setConfirmDisable] = useState<{ open: boolean; row: any | null, mode: string | null }>({
        open: false,
        row: null,
        mode: null
    });


    const [loading, setLoading] = useState(false);
    const [scrapingDetails, setScrapingDetails] = useState<any[]>([]);

    const rowsToDisplay = useMemo(() => {
        const rowsArray = Array.isArray(modalScraping.row)
            ? modalScraping.row
            : modalScraping.row ? [modalScraping.row] : [];

        let result: any[] = [];
        for (const r of rowsArray) {
            const hasActive = r.scraping_details?.some((s: any) => s.status);
            if (hasActive) {
                result.push(...r.scraping_details.map((s: any, i: number) => ({
                    ...s,
                    id: s.id ?? `${r.id}-react-${i}`,
                    status: false,
                })));
            } else {
                result.push(...r.country_name.map((country: string, i: number) => ({
                    id: `${r.id}-new-${i}`,
                    keyword: r.keyword,
                    platform_scraping: ["Google"],
                    country,
                    frequency: "2 hours",
                    device: ["desktop", "mobile"],
                    status: true,
                    country_code: r.country_code,
                })));
            }
        }
        return result;
    }, [modalScraping]);

    const mergedRows = useMemo(() => {
        return data.map((row) =>
            localPatches[row.id] ? { ...row, ...localPatches[row.id] } : row
        );
    }, [data, localPatches]);

    const handleToggle = useCallback((row: any, currentStatus: boolean) => {
        if (currentStatus) {
            setConfirmDisable({ open: true, row, mode: "Scraping" });
        } else {
            setModalScraping({
                open: true,
                row: [row],
            });
        }
    }, []);

    const columns: GridColDef[] = useMemo(() => [
        // Keyword
        {
            field: "keyword",
            headerName: t("dashboard.global.keyword"),
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        {t("dashboard.global.keyword")}
                    </Typography>

                    <Iconify
                        icon="proicons:cursor-click"
                        width={20}
                        height={20}
                        className="keyword-header-icon"
                    />
                </Box>
            ),
            headerClassName: "keyword-header",
            minWidth: 250,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" px={1}>
                    <span>{params.row.keyword}</span>
                    <Typography fontSize={14} color="#919EAB">{formatNumber(params.row.clicks_last30d)}</Typography>
                </Box>
            )
        },
        {
            field: "keyword_id", headerName: "Keyword ID", minWidth: 200, type: "number"
        },
        {
            field: "match_type",
            headerName: "Match Type",
            minWidth: 200,
            type: "string",
            filterable: true,
            renderCell: (params) => (
                <Box>
                    <span>
                        {params.row.match_type
                            ? params.row.match_type.charAt(0).toUpperCase() +
                            params.row.match_type.slice(1).toLowerCase()
                            : ""}
                    </span>
                </Box>
            )

        },
        // AD COST
        {
            field: "cost_last30d",
            filterable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Ad Cost
                    </Typography>
                </Box>
            ),
            headerName: "Ad Cost",
            renderCell: ({ row }) => {
                return (
                    <>{formatCurrency(row.cost_last30d, workspace?.currency, lang, true, 2)}</>
                );
            },
            minWidth: 200,
            type: "number"
        },
        // AVG. CPC
        {
            field: "avg_cpc",
            filterable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Avg. CPC
                    </Typography>
                </Box>
            ),
            headerName: "Avg. CPC",
            renderCell: ({ row }) => {
                return (
                    <>{formatCurrency(row.avg_cpc, workspace?.currency, lang, true, 2)}</>
                );
            },
            minWidth: 200,
            type: "number"
        },
        // Region
        {
            field: "region_name",
            headerName: "Region",
            minWidth: 200,
            renderCell: ({ row }) => {
                const flag = countriesJson.find((c) => c.name === row.region_name)?.code?.toLowerCase();
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        {flag ? <FlagIcon isCircle={false} code={flag} sx={{ width: 16, height: 16 }} /> : <Iconify icon="flat-color-icons:globe" sx={{ width: 20, height: 20 }} />}
                        {row.region_name}
                    </Box>
                );
            }
        },
        // Campaigns
        {
            field: "campaign_names",
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign
                    </Typography>
                </Box>
            ),
            headerName: "Campaign",
            minWidth: 230,
            flex: 1,
            renderCell: ({ value, row }) => {
                const flag = countriesJson.find((c) => c.name === row?.region_name)?.code?.toLowerCase();

                return (
                    <CustomTooltip title={value} placement="top" arrow>
                        <Box
                            sx={{ "&:hover": { cursor: "pointer" } }}
                            display="flex"
                            alignItems="center"
                            px="12px"
                            overflow="hidden"
                            minWidth={0}
                            width="100%"
                        >
                            {(value || []).map((c: string, i: number) => (
                                <Box key={i} display="flex" alignItems="center" overflow="hidden" minWidth={0}>
                                    <Box sx={{ mr: "12px" }}>
                                        {flag ? (
                                            <FlagIcon
                                                isCircle={false}
                                                code={flag}
                                                sx={{ width: 22, height: 16 }}
                                            />
                                        ) : (
                                            <Iconify
                                                icon="flat-color-icons:globe"
                                                sx={{ width: 20, height: 20 }}
                                            />
                                        )}
                                    </Box>

                                    <Box
                                        component="span"
                                        sx={{
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            display: "block",
                                        }}
                                    >
                                        {c}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CustomTooltip>
                );
            }
        },
        { field: "campaign_ids", headerName: "Campaign ID", flex: 1 },
        { field: "ad_group_names", headerName: "Ad Group Name", flex: 1 },
        { field: "ad_group_ids", headerName: "Ad Group ID", flex: 1 },
        // Scraping Status
        {
            field: "scraping_status",
            headerName: "Scraping Status",
            filterable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                        flexDirection: "column"
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Scraping
                    </Typography>
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Status
                    </Typography>
                </Box>
            ),
            type: 'boolean',
            minWidth: 50,
            renderCell: ({ row }) => {
                const activeDetail = row.scraping_details?.find((s: any) => s.status === true);
                const freq = activeDetail?.frequency;

                const switchComponent = (
                    <Box
                        px="12px"
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <GreenCheckSwitch
                            checked={!!activeDetail}
                            onChange={() => handleToggle(row, !!activeDetail)}
                        />
                    </Box>
                );

                if (activeDetail) {
                    return (
                        <CustomTooltip
                            title={freq ? `Frequency: ${freq}` : "No frequency"}
                            placement="top"
                            arrow
                        >
                            {switchComponent}
                        </CustomTooltip>
                    );
                }

                return switchComponent;
            },
        },
        {
            field: "mode",
            headerName: "Mode",
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                        flexDirection: "column"
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Mode
                    </Typography>
                </Box>
            ),
            minWidth: 200,
            sortable: false,
            editable: false,
            filterable: false,

            renderCell: (params: any) => {
                const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

                const activeDetails =
                    params.row.activations_details?.filter((d: any) => d.status === true) || [];
                const hasActive = activeDetails.length > 0;

                const activeModes = params.row.activations_details
                    ?.filter((d: any) => d.status)
                    ?.map((d: any) => d.mode) ?? [];

                const hasActiveMode = activeModes.includes("Active Mode");
                const hasKoManual = activeModes.includes("KO Bidding Manual");
                const hasKoAuto = activeModes.some((m: any) => m.startsWith("KO Bidding Auto"));

                return (
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            overflow: "hidden",
                            px: 1,
                        }}
                    >
                        {hasActive ? (
                            activeDetails.map((detail: any, i: number) => (
                                <CustomTooltip
                                    key={i}

                                    arrow
                                    placement="top"
                                    disableInteractive={false}
                                    open={hoveredIndex === i}
                                    title={
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                alignItems: "center",
                                                pointerEvents: "auto"
                                            }}
                                            onMouseEnter={() => setHoveredIndex(i)}
                                            onMouseLeave={() => setHoveredIndex(null)}
                                        >
                                            {detail.mode !== "Active Mode" && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                                                    onClick={(e) => {
                                                        detail.mode === "KO Bidding Manual"
                                                            ? setModalActivationManual({
                                                                open: true,
                                                                isActive: true,
                                                                row: params.row,
                                                            })
                                                            : setModalActivationAuto({
                                                                open: true,
                                                                isActive: true,
                                                                row: params.row,
                                                            });
                                                    }}
                                                >
                                                    <Iconify icon="solar:pen-bold" width={18} />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmDisable({
                                                        open: true,
                                                        row: params.row,
                                                        mode: detail.mode,
                                                    });
                                                }}
                                            >
                                                <Iconify icon="solar:trash-bin-minimalistic-bold" width={18} />
                                            </IconButton>
                                        </Box>
                                    }
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                pointerEvents: "auto",
                                                p: 0.6,
                                                borderRadius: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                zIndex: 2500,
                                            },
                                        },
                                    }}
                                >
                                    <Typography
                                        onMouseEnter={() => setHoveredIndex(i)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                        sx={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            py: 0.4,
                                            height: 28,
                                            display: "flex",
                                            alignItems: "center",

                                            color:
                                                detail?.mode?.startsWith("KO Bidding Auto")
                                                    ? "rgba(122, 37, 228, 1)"
                                                    : detail.mode === "KO Bidding Manual"
                                                        ? "rgba(48, 57, 176, 1)"
                                                        : "rgba(103, 83, 21, 1)",

                                            background:
                                                detail?.mode?.startsWith("KO Bidding Auto")
                                                    ? "rgba(164, 111, 247, 0.19)"
                                                    : detail.mode === "KO Bidding Manual"
                                                        ? "rgba(111, 120, 247, 0.19)"
                                                        : "rgba(250, 176, 0, 0.08)",

                                            borderRadius: "6px",
                                            fontSize: 13,
                                            fontWeight: 500,
                                            textAlign: "center",
                                            transition: "all 0.2s ease",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: "4px",
                                                    p: .5,
                                                    background:
                                                        detail?.mode?.startsWith("KO Bidding Auto")
                                                            ? "rgba(164, 111, 247, 0.2)"
                                                            : detail.mode === "KO Bidding Manual"
                                                                ? "rgba(111, 120, 247, 0.2)"
                                                                : "rgba(250, 176, 0, 0.1)",
                                                }}
                                            >
                                                {detail.mode === "Active Mode"
                                                    ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                                                            <path d="M14.0648 15.0653C12.6411 16.0997 10.9264 16.6569 9.16658 16.6569C7.40675 16.6569 5.69209 16.0997 4.26836 15.0653M12.8197 0.833577C14.4014 1.60503 15.6983 2.85743 16.5245 4.41126C17.3507 5.9651 17.6637 7.7406 17.4188 9.4833M0.914422 9.48322C0.669501 7.74052 0.98257 5.96502 1.80876 4.41118C2.63495 2.85735 3.93184 1.60495 5.51356 0.833496M13.7499 8.32353C13.7499 10.8548 11.6979 12.9069 9.16658 12.9069C6.63527 12.9069 4.58324 10.8548 4.58324 8.32353C4.58324 5.79222 6.63527 3.7402 9.16658 3.7402C11.6979 3.7402 13.7499 5.79222 13.7499 8.32353Z" stroke="#AF8604" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                        </svg>
                                                    )
                                                    : detail.mode === "KO Bidding Manual"
                                                        ? <Iconify icon="mingcute:pencil-3-line" />
                                                        : <Iconify icon="material-symbols:shield-outline-rounded" />
                                                }
                                            </Box>

                                            <Typography sx={{ px: 1 }} fontSize={13}>
                                                {detail?.mode?.startsWith("KO Bidding Auto")
                                                    ? "Auto"
                                                    : detail.mode === "KO Bidding Manual"
                                                        ? "Manual"
                                                        : "Active"
                                                }
                                            </Typography>
                                        </Box>
                                    </Typography>

                                </CustomTooltip>
                            ))
                        ) : (
                            <Typography
                                sx={{
                                    flex: 1,
                                    color: "rgba(255,255,255,0.4)",
                                    borderRadius: "6px",
                                    px: 1.2,
                                    py: 0.4,
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textAlign: "left",
                                }}
                            >
                                --
                            </Typography>
                        )}

                        {!(hasActiveMode && (hasKoManual || hasKoAuto)) && (

                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setModalCreateStrategy({
                                        open: true,
                                        row: [params.row],
                                    });
                                }}
                                sx={{
                                    position: "absolute",
                                    right: 6,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: 22,
                                    height: 22,
                                    borderRadius: "6px",
                                    backgroundColor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    opacity: 0,
                                    transition: "all 0.2s ease",
                                    "&:hover": { backgroundColor: "white" },
                                    ".MuiDataGrid-cell:hover &": { opacity: 1 },
                                }}
                            >
                                <Typography sx={{ fontSize: 20, lineHeight: 1 }}>+</Typography>
                            </Box>
                        )}
                    </Box>
                );
            }
        },
        // -----------------------
        // Colonne Max CPC à la fin
        {
            field: "max_cpc",
            type: "number",
            headerName: "Max CPC",
            renderHeader: () => (
                <div
                    style={{
                        width: "100%",
                        whiteSpace: "nowrap",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        textAlign: "right",
                    }}
                >
                    <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="rgba(99, 115, 129, 1)"
                    >
                        Max CPC
                    </Typography>
                </div>
            ),
            minWidth: 80,
            editable: true,
            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                );

                const koAuto = activations.find(
                    (d: any) => d.status === true && d.mode?.startsWith("KO Bidding Auto")
                );

                const value = koManual?.max_cpc ?? (koAuto ? 666 : null);
                const isClickable = Boolean(koManual);
                const isAuto = !koManual && Boolean(koAuto);

                return (
                    <Box display="flex" height="100%" py={1} px={0.5}>
                        <Box
                            sx={{
                                borderRadius: "6px",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "6px 8px",
                                transition: "all 0.15s ease",
                                border: isClickable
                                    ? "1px solid #338AF3"
                                    : "1px solid rgba(145, 158, 171, 0.16)",
                                cursor: isClickable ? "pointer" : "not-allowed",
                                ...(isClickable && {
                                    "&:hover": {
                                        border: "1px solid rgba(0, 108, 156, 0.50)",
                                    },
                                }),
                            }}
                        >
                            {value !== null ? (
                                isAuto ? (
                                    <span style={{ opacity: 0.5 }}>{params?.row?.max_cpc}</span>
                                ) : (
                                    formatCurrency(value, workspace?.currency, lang, true, 2)
                                )
                            ) : (
                                <span style={{ opacity: 0.5 }}>--</span>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
    ], [workspace?.currency, lang]);


    const handleDisableActiveMode = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableActiveMode({
                    id: detail.id,
                    workspace_id: workspace.id,
                }).unwrap();

                enqueueSnackbar(`Active Mode disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        activations_details: rowToPatch.activations_details.filter(
                            (d: any) => d.mode !== "Active Mode"
                        )
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Active Mode`, { variant: "error" });
            }
        },
        [disableActiveMode, workspace, confirmDisable.row, setLocalPatches]
    );

    const handleConfirmScraping = useCallback(async () => {
        const rows = modalScraping.row;
        if (!rows || !workspace) return;

        setLoading(true);

        try {
            const details = scrapingDetails.length > 0 ? scrapingDetails : rowsToDisplay;

            const payloadArray: any = details.map((detail, index) => {
                const originalRow = rows[index] ?? rows[0];

                return {
                    keyword: detail.keyword ?? originalRow.keyword,
                    country: Array.isArray(detail.country) ? detail.country : [detail.country],
                    country_code: originalRow.country_code,
                    home_language: originalRow.home_language[0],
                    frequency: detail.frequency ?? "60 min",
                    platform_scraping: detail.platform_scraping ?? ["Google"],
                    device: detail.device ?? ["desktop", "mobile"],
                    workspaceId: workspace.id,
                    client: workspace.bqTable,
                    mode: "Scraping",
                };
            });

            const result = await addScraping(payloadArray).unwrap();

            enqueueSnackbar(`Scraping added successfully.`, { variant: "success" });

            rows.forEach((r: any, rowIndex: any) => {
                setLocalPatches(prev => ({
                    ...prev,
                    [r.id]: {
                        ...prev[r.id],
                        scraping_details: result.map((newScraping: any) => ({
                            ...newScraping,
                            status: true
                        }))
                    }
                }));
            });

            handleCloseScraping();

        } catch (err) {
            console.error(err);
            enqueueSnackbar(`Failed to add scraping`, { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [
        modalScraping.row,
        workspace,
        scrapingDetails,
        rowsToDisplay,
        addScraping,
        setLocalPatches,
        handleCloseScraping
    ]);

    const handleDisableScraping = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableScraping({ id: detail.id }).unwrap();
                enqueueSnackbar(`Scraping disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        scraping_details: []
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Scraping`, { variant: "error" });
            }
        },
        [disableScraping, workspace, confirmDisable.row, setLocalPatches]
    );

    const handleConfirmDisable = useCallback(
        async (row: any) => {
            if (!row) return;

            const modeToDisable = confirmDisable.mode;

            try {
                if (modeToDisable === "Active Mode") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "Active Mode");
                    await handleDisableActiveMode(detail);
                }

                else if (modeToDisable === "Scraping") {
                    const detail = row.scraping_details?.[0];
                    await handleDisableScraping(detail);
                }

                else if (modeToDisable === "KO Bidding Manual") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "KO Bidding Manual");

                    if (detail?.id) {
                        await disableKoBiddingManualMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Manual disabled successfully`, { variant: "success" });

                        setLocalPatches(prev => {
                            const existing = prev[row.id] ?? row;

                            return {
                                ...prev,
                                [row.id]: {
                                    ...existing,
                                    activations_details: existing.activations_details.filter(
                                        (d: any) => d.mode !== "KO Bidding Manual"
                                    )
                                }
                            };
                        });

                    }
                }

                else if (modeToDisable?.startsWith("KO Bidding Auto")) {
                    const detail = row.activations_details?.find(
                        (d: any) => d.mode?.startsWith("KO Bidding Auto")
                    );

                    if (detail?.id) {
                        await disableKoBiddingAutoMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Auto disabled successfully`, { variant: "success" });

                        // 🔥 Patch local instantané
                        setLocalPatches(prev => ({
                            ...prev,
                            [row.id]: {
                                ...row,
                                activations_details: row.activations_details.filter(
                                    (d: any) => !d.mode?.startsWith("KO Bidding Auto")
                                )
                            }
                        }));
                    }
                }

                setConfirmDisable({ open: false, row: null, mode: null });

            } catch (err) {
                enqueueSnackbar(`Failed to disable ${modeToDisable}`, { variant: "error" });
            }
        },
        [
            confirmDisable,
            handleDisableActiveMode,
            handleDisableScraping,
            disableKoBiddingManualMaxCpc,
            disableKoBiddingAutoMaxCpc,
            setLocalPatches
        ]
    );

    const CustomToolbar = ({ selectionRows = [] }: any) => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    {/* <GridToolbarExport csvOptions={{ allColumns: true }} /> */}
                </Box>

                {selectionRows.length > 1 && (
                    <Box sx={{ pr: 2 }}>
                        <Button
                            onClick={() =>
                                setModalScraping({
                                    open: true,
                                    row: selectionRows,
                                })
                            }
                            sx={{ mr: 1 }}
                            size="small"
                            variant="outlined"
                        >
                            Active Scraping ({selectionRows.length})
                        </Button>

                        <Button onClick={() =>
                            setModalCreateStrategy({
                                open: true,
                                row: selectionRows,
                            })
                        }
                            size="small"
                            variant="contained"
                        >
                            Activate Bidding Strategy ({selectionRows.length})
                        </Button>
                    </Box>
                )}
            </GridToolbarContainer>
        );
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem("columnVisibilityModel");
        return saved ? JSON.parse(saved) : {
            campaign_ids: false,
            ad_group_names: false,
            ad_group_ids: false,
            keyword_id: false,
            region_name: false,
            avg_cpc: false,
            cost_last30d: false,
        };
    });

    const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        localStorage.setItem("columnVisibilityModel", JSON.stringify(newModel)); // persistance
    }, []);

    return (
        <Box
            sx={{
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainer": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                },

                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainerContent": {
                    display: "flex",
                    flex: 1,
                },
                "& .MuiDataGrid-columnHeader": {
                    backgroundColor: 'rgba(250, 251, 251)',
                    border: "1px solid #e9ecee"
                },
                "& .keyword-header-wrapper": {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                },
                position: "relative",
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell--pinnedLeft": {
                    backgroundColor: "white",
                },
                "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    border: "1px solid #e9ecee"

                },
                "& .MuiDataGrid-row": {
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                },
                "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "1px solid rgba(255,255,255,0.10)",

                },
                "& .MuiDataGrid-columnSeparator": {
                    color: "rgba(255,255,255,0.12)",
                    visibility: "visible",
                },

                "& .MuiDataGrid-cell:not(:last-of-type)": {
                    borderRight: "1px solid rgba(255,255,255,0.06)",
                },
            }}
        >
            <DataGridPro
                rowHeight={60}
                autoHeight
                getRowId={(r) => r.id}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onPaginationModelChange={({ page, pageSize }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => <CustomToolbar selectionRows={selectionRows} />,
                    filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection: any) => {
                    const selectedIds = Array.isArray(newSelection) ? newSelection : [];
                    setSelectionRows(
                        mergedRows.filter((row) => selectedIds.includes(row.id))
                    );
                }}
                disableRowSelectionOnClick
                onCellClick={(params, event) => {
                    if (params.field !== "max_cpc") return;

                    const row = params?.row;
                    if (!row || !row.activations_details?.length) return;

                    event.stopPropagation();

                    const hasManualWithCpc = row.activations_details.some(
                        (d: any) =>
                            d.mode === "KO Bidding Manual" &&
                            d.status === true &&
                            d.max_cpc !== null &&
                            d.max_cpc !== undefined
                    );

                    if (hasManualWithCpc) {
                        setModalActivationManual({
                            open: true,
                            isActive: true,
                            row,
                        });
                    }
                }}

                columns={columns}
                sx={{
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e9ecee",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                    },
                }}
            />

            <ModalScraping
                open={modalScraping.open}
                loading={loading}
                rowsToDisplay={rowsToDisplay}
                onClose={handleCloseScraping}
                onConfirm={handleConfirmScraping}
                setModalScraping={setModalScraping}
                setScrapingDetails={setScrapingDetails}
            />

            <ModalActivationsManual
                open={modalActivationManual.open && modalActivationManual.isActive}
                row={modalActivationManual.row}
                onClose={handleCloseActivationManual}
                setLocalPatches={setLocalPatches}
            />

            <ModalCreateStrategyOther
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />
        </Box>
    );
}
